import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  useAuthenticationSessionEndMutation,
  useDashboardTaskCounterQuery,
} from 'graphql-common';
import { useApolloClient } from '@apollo/client';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import DashboardLayoutCommon from '@lib/layouts/DashboardLayout/DashboardLayout';
import { DebugModeProvider, useNavigationLogger } from '@lib/services/logger';
import { captureException } from 'utils/captureException';
import useWhoamiQueryHook from 'utils/fetch/hooks/useWhoamiQueryHook';
import { useLocation, useParams } from 'react-router-dom';
import getNavItems, {
  getSettingsNavItems,
} from 'layouts/DashboardLayout/utils/getNavItems';
import getProfileMenuItems from 'layouts/DashboardLayout/utils/getProfileMenuItems';
import PlatformSwitcher, {
  Platforms,
} from '@lib/components/PlatformSwitcher/PlatformSwitcher';
import {
  checkUserAccessByComponent,
  checkUserAccessForPage,
  checkUserAccessItemIdsByComponent,
} from '@lib/utils/accesses';
import getHomePageUrl from 'layouts/DashboardLayout/utils/getHomePageUrl';
import useTechnicalPause from '@lib/hooks/useTechnicalPause';
import JobTracker from 'components/JobTracker/JobTracker';
import { APP_URLS } from 'constants/urls';
import { removeIndexedDB } from 'utils/indexedDBUtils';

const { VITE_AUTH_PLATFORM_URL: AUTH_PLATFORM } = import.meta.env;

function DashboardLayout(): React.ReactElement {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const { pathname } = useLocation();
  const { id = '', ...restParams } = useParams();
  const { activeTechnicalPause, upcomingTechnicalPause } =
    useTechnicalPause(captureException);

  const { currentUser } = useWhoamiQueryHook({
    fetchPolicy: 'cache-only',
  });

  // Debug mode logging
  useNavigationLogger();

  const getUserAccessByUrl = useCallback(
    (pathnameStr: string) =>
      checkUserAccessForPage(currentUser?.finalAccesses ?? [], pathnameStr),
    [currentUser?.finalAccesses],
  );

  const getUserAccessByComponent = useCallback(
    (component: AccessComponentEnum) =>
      checkUserAccessByComponent(currentUser?.finalAccesses ?? [], component),
    [currentUser?.finalAccesses],
  );

  const getUserAccessItemIdsByComponent = useCallback(
    (component: AccessComponentEnum) =>
      checkUserAccessItemIdsByComponent(
        currentUser?.finalAccesses ?? [],
        component,
      ),
    [currentUser?.finalAccesses],
  );

  const getUserAccessByCompany = useCallback(
    (component: AccessComponentEnum) =>
      checkUserAccessByComponent(
        currentUser?.company?.plan?.accesses ?? [],
        component,
      ),
    [currentUser?.company?.plan?.accesses],
  );
  const logoUrl = getHomePageUrl(currentUser?.finalAccesses ?? []);

  const countersQuery = useDashboardTaskCounterQuery();
  const myApprovalsCounter = _get(
    countersQuery,
    'data.myApprovals.metadata.totalCount',
    0,
  );

  const callbackOnLogout = async () => {
    localStorage.clear();
    await removeIndexedDB();
    window.location.href = AUTH_PLATFORM;
  };

  const [logoutMutation, logoutMutationResult] =
    useAuthenticationSessionEndMutation({
      onCompleted: () => {
        apolloClient.clearStore().then(callbackOnLogout);
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
        callbackOnLogout();
      },
    });

  const onLogoutHandler = useCallback(
    () => logoutMutation({ variables: { input: {} } }),
    [logoutMutation],
  );

  const navItems = useMemo(
    () =>
      getNavItems({
        getUserAccessByCompany,
        getUserAccessByComponent,
        id,
        myApprovalsCounter,
        pathname,
        t,
        urlParams: restParams,
      }),
    [
      getUserAccessByCompany,
      getUserAccessByComponent,
      id,
      myApprovalsCounter,
      pathname,
      t,
      restParams,
    ],
  );

  const settingsMenuNavItems = useMemo(
    () =>
      getSettingsNavItems({
        getUserAccessByCompany,
        getUserAccessByComponent,
        id,
        pathname,
        t,
      }),
    [getUserAccessByCompany, getUserAccessByComponent, id, pathname, t],
  );

  const profileMenuItems = useMemo(
    () =>
      getProfileMenuItems({
        logoutLoading: logoutMutationResult.loading,
        onLogoutHandler,
        t,
      }),
    [logoutMutationResult.loading, onLogoutHandler, t],
  );

  const outletContext = useMemo(
    () => ({
      getUserAccessByCompany,
      getUserAccessByComponent,
      getUserAccessByUrl,
      getUserAccessItemIdsByComponent,
      logoutLoading: logoutMutationResult.loading,
      onLogoutHandler,
    }),
    [
      getUserAccessByCompany,
      getUserAccessByComponent,
      getUserAccessByUrl,
      getUserAccessItemIdsByComponent,
      logoutMutationResult.loading,
      onLogoutHandler,
    ],
  );

  const tools = useMemo(
    () => (
      <PlatformSwitcher selected={Platforms.Admin} available={Platforms.User} />
    ),
    [],
  );

  const settingsPaths = useMemo(
    () => [
      APP_URLS.dashboard.formChecklists.add.path,
      APP_URLS.dashboard.formChecklists.edit.getDynamicPath({
        pathParts: { id },
      }),
    ],
    [id],
  );

  const disableContentScroll = useMemo(
    () =>
      APP_URLS.dashboard.formTemplates.editVersion.isTheSameUrlAs(pathname, {
        pathParts: { id, versionId: restParams?.versionId },
      }) ||
      APP_URLS.dashboard.formTemplates.add.isTheSameUrlAs(pathname) ||
      APP_URLS.dashboard.dataImport.isTheSameUrlAs(pathname) ||
      APP_URLS.dashboard.interventions.view.isTheSameUrlAs(pathname, {
        pathParts: { id },
      }) ||
      APP_URLS.dashboard.interventions.viewActions.isTheSameUrlAs(pathname, {
        pathParts: { id },
      }) ||
      APP_URLS.dashboard.assets.counters.isTheSameUrlAs(pathname) ||
      APP_URLS.dashboard.homeWidgets.isTheSameUrlAs(pathname),
    [pathname, id, restParams?.versionId],
  );

  return (
    <DebugModeProvider
      debugModeUntil={currentUser?.debugModeUntil}
      userId={currentUser?.id}
      userEmail={currentUser?.email}
    >
      <DashboardLayoutCommon
        activeTechnicalPause={activeTechnicalPause}
        captureException={captureException}
        disableContentScroll={disableContentScroll}
        logoUrl={logoUrl}
        navItems={navItems}
        outletContext={outletContext}
        profileMenuItems={profileMenuItems}
        settingsMenuNavItems={settingsMenuNavItems}
        settingsPaths={settingsPaths}
        tools={tools}
        topComponents={<JobTracker />}
        upcomingTechnicalPause={upcomingTechnicalPause}
        user={currentUser}
      />
    </DebugModeProvider>
  );
}

export default React.memo(DashboardLayout);
